import clsx from "clsx";
import { Fragment, type ReactNode } from "react";
import { tokenizeGdRichText, type RichTextSegment } from "@/lib/gdRichText";

function segmentsToReact(segments: RichTextSegment[], keyPrefix: string): ReactNode[] {
  return segments.map((segment, index) => {
    const key = `${keyPrefix}-${index}`;
    if (segment.type === "text") {
      if (!segment.value) return null;
      const parts = segment.value.split("\n");
      if (parts.length === 1) {
        return <Fragment key={key}>{segment.value}</Fragment>;
      }
      return (
        <Fragment key={key}>
          {parts.map((part, lineIndex) => (
            <Fragment key={`${key}-l${lineIndex}`}>
              {lineIndex > 0 ? <br /> : null}
              {part}
            </Fragment>
          ))}
        </Fragment>
      );
    }
    const children = segmentsToReact(segment.children, key);
    if (segment.type === "bold") return <strong key={key}>{children}</strong>;
    if (segment.type === "italic") return <em key={key}>{children}</em>;
    return <u key={key}>{children}</u>;
  });
}

export function parseGdRichTextLine(text: string): ReactNode[] {
  if (!text) return [];
  return segmentsToReact(tokenizeGdRichText(text), "rt");
}

export function GdRichTextInline({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const nodes = parseGdRichTextLine(text);
  if (!nodes.length) return null;
  return <span className={className}>{nodes}</span>;
}

export function GdRichTextParagraphs({
  text,
  className,
  paragraphClassName,
}: {
  text: string;
  className?: string;
  paragraphClassName?: string;
}) {
  const lines = text.split("\n");
  return (
    <div className={className}>
      {lines.map((line, index) => (
        <p key={`line-${index}`} className={paragraphClassName}>
          {line ? <GdRichTextInline text={line} /> : "\u00A0"}
        </p>
      ))}
    </div>
  );
}

export function GdRichTextToolbarButton({
  label,
  title,
  activeClassName,
  disabled,
  onClick,
}: {
  label: string;
  title: string;
  activeClassName?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      title={title}
      aria-label={title}
      aria-disabled={disabled || undefined}
      className={clsx(
        "gd-rich-text__toolbar-btn",
        activeClassName,
        disabled && "is-disabled",
      )}
      onMouseDown={(event) => {
        event.preventDefault();
        if (!disabled) onClick();
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {label}
    </span>
  );
}
